"use client";

import { useEffect, useRef, type CSSProperties } from "react";
import { ADSENSE_PREVIEW } from "./adsense-config";
import styles from "./ads.module.css";

declare global {
	interface Window {
		adsbygoogle?: unknown[];
	}
}

type GoogleAdsenseUnitProps = {
	slot: string;
	className?: string;
	style?: CSSProperties;
	format?: string | null;
	layout?: string;
	fullWidthResponsive?: boolean | null;
};

const ADSENSE_CLIENT = process.env.NEXT_PUBLIC_GOOGLE_ADSENSE_CLIENT?.trim();

/** 設定済み広告枠を描画し、幅の確定後に一度だけ初期化する。プレビューは模擬枠のみで通信しない。 */
export default function GoogleAdsenseUnit({
	slot,
	className,
	style,
	format = "auto",
	layout,
	fullWidthResponsive = true,
}: GoogleAdsenseUnitProps) {
	const adRef = useRef<HTMLModElement | null>(null);
	const requestedRef = useRef(false);

	useEffect(() => {
		if (ADSENSE_PREVIEW || !ADSENSE_CLIENT || !slot || requestedRef.current || !adRef.current) {
			return;
		}

		const element = adRef.current;
		// 非表示・幅0の枠では初期化しない。サイズ確定後の通知で再評価し、広告の自動更新は行わない。
		const initialize = () => {
			if (requestedRef.current || element.getBoundingClientRect().width <= 0) return;
			requestedRef.current = true;
			if (element.dataset.adsbygoogleStatus === "done") return;
			try {
				(window.adsbygoogle = window.adsbygoogle || []).push({});
			} catch (error) {
				if (element.dataset.adStatus !== "filled") element.dataset.adInitialization = "error";
				if (process.env.NODE_ENV !== "production") {
					console.error("Google Adsense initialization failed", error);
				}
			}
		};
		initialize();
		const observer = new ResizeObserver(initialize);
		observer.observe(element);
		return () => observer.disconnect();
	}, [slot]);

	if (ADSENSE_PREVIEW && slot) {
		return (
			<div className={`${styles.previewAd} ${className ?? ""}`} style={style} role="img" aria-label="広告プレビュー">
				広告プレビュー
			</div>
		);
	}

	if (!ADSENSE_CLIENT || !slot) {
		return null;
	}

	const extraAttributes: Record<string, string> = {};

	if (layout) {
		extraAttributes["data-ad-layout"] = layout;
	}

	return (
		<ins
			ref={adRef}
			className={["adsbygoogle", className ?? ""].join(" ").trim()}
			style={style ?? { display: "block" }}
			data-ad-client={ADSENSE_CLIENT}
			data-ad-slot={slot}
			data-ad-format={format ?? undefined}
			data-full-width-responsive={fullWidthResponsive == null ? undefined : fullWidthResponsive ? "true" : "false"}
			{...extraAttributes}
		/>
	);
}
